import React, { useState } from 'react';
import { Check, Clock, Facebook, Loader2, Mail, MapPin, Phone } from 'lucide-react';
import { business, services } from '../data/site';
import { SectionHeading } from './SectionHeading';
type Status = 'idle' | 'submitting' | 'success' | 'error';
const fieldClass = 'w-full border border-steel-300 bg-white px-4 py-3.5 text-sm text-steel-950 outline-none transition-colors placeholder:text-steel-300 focus:border-brand focus:ring-2 focus:ring-brand/40';
export function ContactSection() {
  const [status, setStatus] = useState<Status>('idle');
  const [form, setForm] = useState({
    name: '',
    phone: '',
    service: '',
    details: ''
  });
  const update = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm(prev => ({
    ...prev,
    [key]: e.target.value
  }));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 900);
  };
  return <section id="contact" className="bg-steel-50 py-20 lg:py-28" data-id="element-1" data-mp="2od6x3gywnqm">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-5 lg:grid-cols-[1fr_1fr] lg:gap-20 lg:px-10" data-id="element-2" data-mp="gnclfiarnm92">
        <div data-id="element-3" data-mp="ckzs5td7hgt6">
          <SectionHeading eyebrow="Get A Quote" tone="light" title={<>
                Tell us what
                <br data-id="element-5" data-mp="qjpp9900zghj" />
                needs welding
              </>} description="Send the details and we'll get back to you with a straight price. Photos speed things up — text them to the number below." data-id="element-4" data-mp="ck6fycvh9z3n" />

          <dl className="mt-12 space-y-px border border-steel-200 bg-steel-200" data-id="element-6" data-mp="109irvki6wl9n">
            <div className="flex items-center gap-4 bg-white p-5" data-id="element-7" data-mp="vcs8oob399k0">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-brand" data-id="element-8" data-mp="19b8rveq50sj1">
                <Phone className="h-5 w-5 text-white" aria-hidden="true" data-id="element-9" data-mp="14cff5oiod5jx" />
              </span>
              <div data-id="element-10" data-mp="1yzn2yybxl9t">
                <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-steel-400" data-id="element-11" data-mp="1zj3ucc8u3m9">
                  Call or Text
                </dt>
                <dd className="font-display text-xl font-semibold tracking-tight text-steel-950" data-id="element-12" data-mp="6yc6tatwmc86">
                  <a href={business.phoneHref} className="hover:underline" data-id="element-13" data-mp="ayebztyulns7">
                    {business.phone}
                  </a>
                </dd>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-white p-5" data-id="element-14" data-mp="koz9tf6p6oa2">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-steel-950" data-id="element-15" data-mp="pnsld22fc57l">
                <Mail className="h-5 w-5 text-white" aria-hidden="true" data-id="element-16" data-mp="unezddq2tphg" />
              </span>
              <div data-id="element-17" data-mp="ypjpi2hbg3zc">
                <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-steel-400" data-id="element-18" data-mp="ynywwiekav44">
                  Email
                </dt>
                <dd className="text-base text-steel-950" data-id="element-19" data-mp="2whe5zls97int">
                  <a href={`mailto:${business.email}`} className="hover:underline" data-id="element-20" data-mp="30hephku7mk1q">
                    {business.email}
                  </a>
                </dd>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-white p-5" data-id="element-21" data-mp="2o5m2iun0nq7v">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-steel-950" data-id="element-22" data-mp="2j6sx7phvv5is">
                <Clock className="h-5 w-5 text-white" aria-hidden="true" data-id="element-23" data-mp="2f68yihhnvtuj" />
              </span>
              <div data-id="element-24" data-mp="3bwhzs7a8rdmi">
                <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-steel-400" data-id="element-25" data-mp="3bux5miru9wp6">
                  Hours
                </dt>
                <dd className="text-base text-steel-950" data-id="element-26" data-mp="36uspituuf3zh">{business.hours}</dd>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-white p-5" data-id="element-27" data-mp="2xw75svdmxix4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-steel-950" data-id="element-28" data-mp="2xvxg78wi5dry">
                <MapPin className="h-5 w-5 text-white" aria-hidden="true" data-id="element-29" data-mp="1rhg08edkh9wr" />
              </span>
              <div data-id="element-30" data-mp="1mfqn8t6sam61">
                <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-steel-400" data-id="element-31" data-mp="1mjej3yzpbknx">
                  Based In
                </dt>
                <dd className="text-base text-steel-950" data-id="element-32" data-mp="3f4q32kd8mxy5">{business.base}</dd>
              </div>
            </div>
          </dl>

          <p className="mt-8 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-steel-400" data-id="element-33" data-mp="31emgfsag5286">
            <Facebook className="h-3.5 w-3.5" aria-hidden="true" data-id="element-34" data-mp="35cahld2qfr8i" />
            Facebook: {business.facebook}
          </p>
        </div>

        <div className="bg-steel-950 p-8 lg:p-10" data-id="element-35" data-mp="1yg4g8455coe">
          {status === 'success' ? <div className="flex h-full min-h-[420px] flex-col items-start justify-center" data-id="element-36" data-mp="3k3jd22tbg6tk">
              <span className="flex h-14 w-14 items-center justify-center bg-brand" data-id="element-37" data-mp="3k5vwylimwr99">
                <Check className="h-7 w-7 text-white" aria-hidden="true" data-id="element-38" data-mp="3p4qku1vgl608" />
              </span>
              <h3 className="mt-8 font-display text-3xl font-bold uppercase leading-none text-white" data-id="element-39" data-mp="28qgyfdas1rc1">
                Request sent
              </h3>
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-steel-300" data-id="element-40" data-mp="255oxq81cbb6i">
                Thanks, {form.name.split(' ')[0] || 'friend'}. {business.owner} will reach out at{' '}
                {form.phone} — usually within a few hours. Need it sooner? Call{' '}
                <a href={business.phoneHref} className="text-brand-light hover:underline" data-id="element-41" data-mp="2a5jmo61k1qox">
                  {business.phone}
                </a>
                .
              </p>
              <button type="button" onClick={() => {
            setForm({
              name: '',
              phone: '',
              service: '',
              details: ''
            });
            setStatus('idle');
          }} className="mt-8 border border-white/25 px-6 py-3.5 font-display text-xs font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:border-brand hover:text-brand-light" data-id="element-42" data-mp="1mdw5ifu3htzu">
                Send Another
              </button>
            </div> : <form onSubmit={handleSubmit} noValidate data-id="element-43" data-mp="yq4f68bpzgfk">
              <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-white" data-id="element-44" data-mp="2yt5o9grgnytj">
                Request an Estimate
              </h3>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-steel-400" data-id="element-45" data-mp="33pmb87mxc751">
                Free on-site estimates
              </p>

              <div className="mt-8 space-y-5" data-id="element-46" data-mp="3cizb5tjeo90m">
                <div data-id="element-47" data-mp="3clv9vk32v1q4">
                  <label htmlFor="name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-steel-300" data-id="element-48" data-mp="2fu1hkene6h51">
                    Name *
                  </label>
                  <input id="name" name="name" value={form.name} onChange={update('name')} className={fieldClass} placeholder="Jason DeLong" required data-id="element-49" data-mp="2kupflka1fy8r" />
                </div>
                <div data-id="element-50" data-mp="2tuudn3tcdka9">
                  <label htmlFor="phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-steel-300" data-id="element-51" data-mp="2tr6k44nwcxvj">
                    Phone *
                  </label>
                  <input id="phone" name="phone" type="tel" value={form.phone} onChange={update('phone')} className={fieldClass} placeholder="(254) 000-0000" required data-id="element-52" data-mp="95er4l15ohqf" />
                </div>
                <div data-id="element-53" data-mp="e8xot1tml4sq">
                  <label htmlFor="service" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-steel-300" data-id="element-54" data-mp="eai8txibd5cc">
                    Job Type
                  </label>
                  <select id="service" name="service" value={form.service} onChange={update('service')} className={fieldClass} data-id="element-55" data-mp="5drgnw6vuan1">
                    <option value="" data-id="element-56" data-mp="3rnbven9hvjki">Select a service</option>
                    {services.map(service => <option key={service.title} value={service.title} data-id="element-57" data-mp="3mnz8y3mjceig">
                        {service.title}
                      </option>)}
                    <option value="Other" data-id="element-58" data-mp="3infc715vjcwu">Something else</option>
                  </select>
                </div>
                <div data-id="element-59" data-mp="1kvazvdn7mwur">
                  <label htmlFor="details" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-steel-300" data-id="element-60" data-mp="1kui395hytnqc">
                    Job Details
                  </label>
                  <textarea id="details" name="details" rows={5} value={form.details} onChange={update('details')} className={`${fieldClass} resize-none`} placeholder="Location, what needs built or repaired, and when you need it done." data-id="element-61" data-mp="204k9rm1n118h" />
                </div>
              </div>

              {status === 'error' && <p role="alert" className="mt-5 border-l-2 border-red-500 pl-3 text-sm text-red-400" data-id="element-62" data-mp="1vb7q7qbtfkml">
                  Please add your name and a phone number so we can reach you.
                </p>}

              <button type="submit" disabled={status === 'submitting'} className="mt-8 inline-flex w-full items-center justify-center gap-3 bg-brand px-6 py-5 font-display text-sm font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:bg-white hover:text-black disabled:cursor-not-allowed disabled:opacity-70" data-id="element-63" data-mp="1lg5w0g2onsd5">
                {status === 'submitting' ? <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-64" data-mp="36rpaks251wsv" />
                    Sending
                  </> : 'Send Request'}
              </button>
              <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-steel-500" data-id="element-65" data-mp="3bxuff7dl6xcv">
                Or call {business.phone} — we answer in the shop
              </p>
            </form>}
        </div>
      </div>
    </section>;
}